import React, { useState, useEffect } from 'react';
import { Package, ToggleLeft, ToggleRight, Trash2, Search, RefreshCw } from 'lucide-react';
import apiClient from '../api/client';

const ProductosInactivosPage = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    loadProductosInactivos();
  }, []);

  const loadProductosInactivos = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/productos.php?action=inactivos');
      if (response.data.success) {
        setProductos(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar productos inactivos:', error);
      alert('Error al cargar productos inactivos');
    } finally {
      setLoading(false);
    }
  };

  const handleActivar = async (id) => {
    if (!window.confirm('¿Deseas reactivar este producto?')) {
      return;
    }

    try {
      const response = await apiClient.post('/productos.php', {
        action: 'activar',
        id: id
      });
      
      if (response.data.success) {
        alert('✅ Producto reactivado exitosamente');
        setProductos(prevProductos => prevProductos.filter(p => p.id !== id));
      }
    } catch (error) {
      alert('Error al reactivar: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEliminarPermanente = async (id) => {
    if (!window.confirm('⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE.\n\n¿Estás seguro de eliminar PERMANENTEMENTE este producto?')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/productos_hard_delete.php?id=${id}`);
      
      if (response.data.success) {
        alert('🗑️ Producto eliminado permanentemente');
        setProductos(prevProductos => prevProductos.filter(p => p.id !== id));
      }
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleActivarTodos = async () => {
    if (!window.confirm(`¿Deseas reactivar TODOS los ${productos.length} productos inactivos?`)) {
      return;
    }

    try {
      const response = await apiClient.post('/productos.php', {
        action: 'activar_todos'
      });
      
      if (response.data.success) {
        alert(`✅ Se reactivaron ${response.data.count || productos.length} productos`);
        setProductos([]);
      }
    } catch (error) {
      alert('Error al reactivar todos: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLimpiarSinVentas = async () => {
    if (!window.confirm('⚠️ Se eliminarán PERMANENTEMENTE todos los productos inactivos que NO tengan ventas asociadas.\n\n¿Continuar?')) {
      return;
    }

    try {
      const response = await apiClient.post('/productos_hard_delete.php?action=limpiar_inactivos');
      
      if (response.data.success) {
        alert(`🗑️ ${response.data.deleted_count || 0} productos eliminados permanentemente`);
        loadProductosInactivos();
      }
    } catch (error) {
      alert('Error al limpiar: ' + (error.response?.data?.message || error.message));
    }
  };

  const productosFiltrados = productos.filter(producto =>
    producto.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    producto.sku?.toLowerCase().includes(busqueda.toLowerCase()) ||
    producto.codigo_barras?.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-7 h-7 text-gray-600" />
                Productos Inactivos
              </h1>
              <p className="text-gray-600 mt-1">
                {productos.length} productos archivados/inactivos
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
              {productos.length > 0 && (
                <>
                  <button
                    onClick={handleActivarTodos}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <ToggleRight className="w-4 h-4" />
                    Activar Todos
                  </button>
                  <button
                    onClick={handleLimpiarSinVentas}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar Sin Ventas
                  </button>
                </>
              )}
              <button
                onClick={loadProductosInactivos}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Recargar
              </button>
            </div>
          </div>

          {/* Buscador */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre, SKU o código de barras..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Lista de productos */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Cargando productos inactivos...</p>
            </div>
          ) : productosFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {busqueda ? 'No se encontraron productos con esa búsqueda' : 'No hay productos inactivos'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ventas</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {productosFiltrados.map((producto) => (
                    <tr key={producto.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{producto.nombre}</div>
                        <div className="text-sm text-gray-500">{producto.categoria_nombre || 'Sin categoría'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {producto.sku || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {producto.codigo_barras || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        Q {parseFloat(producto.precio_venta || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {producto.stock_total || 0}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {producto.tiene_ventas ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Con ventas
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Sin ventas
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleActivar(producto.id)}
                            className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                            title="Reactivar"
                          >
                            <ToggleRight className="w-4 h-4" />
                          </button>
                          {!producto.tiene_ventas && (
                            <button
                              onClick={() => handleEliminarPermanente(producto.id)}
                              className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar permanentemente"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductosInactivosPage;
